/**
 * Delay before running FE validation after an AI load-building suggestion
 * hydrates the form. Matches McLeod's existing settle window so RHF state
 * (and any post-reset setValue) is ready before we flag missing required fields.
 */
export const LOAD_BUILDING_SUGGESTION_VALIDATION_DELAY_MS = 200;

/**
 * After AI populates a load-building form, schedule the existing front-end
 * validation so unfilled required fields are visible without a submit click.
 *
 * Only schedules when `shouldValidate` is true (create mode + active LB
 * suggestion). Returns a cleanup that clears the timeout — always return it
 * from the hydrate `useEffect` so remounts / suggestion switches do not leak.
 */
export function scheduleLoadBuildingSuggestionValidation(
  shouldValidate: boolean,
  runValidation: () => void | Promise<void>,
  delayMs: number = LOAD_BUILDING_SUGGESTION_VALIDATION_DELAY_MS
): () => void {
  if (!shouldValidate) {
    return () => undefined;
  }

  const timeoutId = window.setTimeout(() => {
    void runValidation();
  }, delayMs);

  return () => {
    window.clearTimeout(timeoutId);
  };
}

/**
 * RHF `validate` helper for required Select fields.
 *
 * Empty values fail. Values that are not in the current option list also fail
 * once options have settled — this catches the bug where
 * AI hydrates a string the Select cannot display, so the UI looks empty while
 * plain `required` still passes.
 */
export function validateRequiredSelectMembership(
  value: unknown,
  optionValues: ReadonlyArray<string> | ReadonlySet<string>,
  options?: {
    isLoading?: boolean;
    message?: string;
  }
): true | string {
  const message = options?.message ?? 'Required';
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return message;
  }

  if (options?.isLoading) {
    return true;
  }

  const entries =
    optionValues instanceof Set
      ? Array.from(optionValues)
      : Array.from(optionValues);
  const allowed = new Set(
    entries.map((entry) => String(entry ?? '').trim()).filter(Boolean)
  );

  if (!allowed.has(trimmed)) {
    return message;
  }

  return true;
}
